"use client";

import * as React from "react";
import { Smartphone } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

type Platform = "ios" | "android";

interface InstallStep {
  number: number;
  text: string;
}

const steps: Record<Platform, { title: string; icon: React.ReactNode; steps: InstallStep[] }> = {
  ios: {
    title: "iPhone (iOS)",
    icon: <AppleLogo className="h-5 w-5" />,
    steps: [
      { number: 1, text: "Abra viny.club no Safari. O app só pode ser instalado pelo Safari no iPhone." },
      { number: 2, text: "Toque no ícone de compartilhar (□↑) na barra inferior da tela." },
      { number: 3, text: 'Role a lista para baixo e toque em "Adicionar à Tela de Início"."' },
      { number: 4, text: 'Confirme tocando em "Adicionar" no canto superior direito. O ícone da Viny Club aparecerá na sua tela inicial.' },
    ],
  },
  android: {
    title: "Android",
    icon: <AndroidIcon className="h-5 w-5" />,
    steps: [
      { number: 1, text: "Abra viny.club no Google Chrome." },
      { number: 2, text: "Toque nos três pontos (⋮) no canto superior direito da tela." },
      { number: 3, text: 'Toque em "Adicionar à tela inicial" ou "Instalar app". O nome pode variar conforme a versão do Chrome.' },
      { number: 4, text: 'Confirme tocando em "Adicionar" ou "Instalar". O ícone aparecerá na sua tela inicial como qualquer outro app.' },
    ],
  },
};

/** Mockup skeleton de celular desenhado em CSS — ilustra onde tocar em cada plataforma. */
function PhoneMockup({ platform, className }: { platform: Platform; className?: string }) {
  const isIos = platform === "ios";
  return (
    <div className={cn("w-[150px] shrink-0 select-none", className)} aria-hidden="true">
      <div className="rounded-[26px] border border-gray-200 bg-gray-50 p-1.5 shadow-sm">
        <div className="relative overflow-hidden rounded-[20px] border border-gray-200/70 bg-white">
          {/* Status bar */}
          <div className="flex items-center justify-between px-3 pt-2">
            <div className="h-1.5 w-6 rounded-full bg-gray-200" />
            {isIos ? (
              <div className="h-2.5 w-8 rounded-full bg-gray-900/80" />
            ) : (
              <div className="flex gap-1">
                <div className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                <div className="h-1.5 w-1.5 rounded-full bg-gray-300" />
              </div>
            )}
          </div>

          {/* Browser bar */}
          <div className="mx-3 mt-2 flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2 py-1.5">
            <div className="h-1.5 w-1.5 rounded-full bg-gray-300" />
            <div className="h-1.5 flex-1 rounded-full bg-gray-200" />
            {!isIos && (
              <div className="flex flex-col items-center gap-[2px]">
                <span className="h-[3px] w-[3px] rounded-full bg-[#9231FF]" />
                <span className="h-[3px] w-[3px] rounded-full bg-gray-400" />
                <span className="h-[3px] w-[3px] rounded-full bg-gray-400" />
              </div>
            )}
          </div>

          {/* Content skeleton */}
          <div className="space-y-2 px-3 py-3">
            <div className="h-2 w-2/3 animate-pulse rounded-full bg-gray-200" />
            <div className="aspect-[4/3] animate-pulse rounded-lg bg-gradient-to-br from-gray-100 via-gray-200/70 to-gray-100" />
            <div className="h-1.5 w-full rounded-full bg-gray-100" />
            <div className="h-1.5 w-4/5 rounded-full bg-gray-100" />
            <div className="h-1.5 w-3/5 rounded-full bg-gray-100" />
          </div>

          {/* iOS share bar */}
          {isIos && (
            <div className="flex items-center justify-around border-t border-gray-100 bg-gray-50/90 px-3 py-2">
              <div className="h-2 w-2 rounded-[3px] bg-gray-300" />
              <div className="h-2 w-2 rounded-full bg-gray-300" />
              {/* Share icon destacado */}
              <div className="relative">
                <div className="absolute -inset-1.5 animate-ping rounded-full bg-[#9231FF]/20" />
                <div className="relative grid h-5 w-5 place-items-center rounded-md bg-[#9231FF]/10 text-[#9231FF]">
                  <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 15V4" />
                    <path d="m8 8 4-4 4 4" />
                    <path d="M6 12H5a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1h-1" />
                  </svg>
                </div>
              </div>
              <div className="h-2 w-2 rounded-[3px] bg-gray-300" />
              <div className="h-2 w-2 rounded-full bg-gray-300" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function InstallContent({ platform, layout = "vertical" }: { platform: Platform; layout?: "horizontal" | "vertical" }) {
  const data = steps[platform];
  const isHorizontal = layout === "horizontal";
  return (
    <div
      className={cn(
        "flex gap-6",
        isHorizontal ? "flex-row items-start" : "flex-col items-center"
      )}
    >
      <PhoneMockup platform={platform} className={isHorizontal ? "sticky top-0" : ""} />
      <ol className={cn("space-y-4", isHorizontal ? "flex-1" : "w-full")}>
        {data.steps.map((step) => (
          <li key={step.number} className="flex gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black text-xs font-bold text-white">
              {step.number}
            </span>
            <p className="text-sm leading-relaxed text-gray-600">{step.text}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function PlatformButton({
  platform,
  className,
}: {
  platform: Platform;
  className?: string;
}) {
  const data = steps[platform];
  return (
    <button
      type="button"
      className={cn(
        "group inline-flex items-center gap-2.5 rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-black transition-colors hover:border-black/30 hover:bg-gray-50",
        className
      )}
    >
      <span className="text-black/80">{data.icon}</span>
      <span>{data.title}</span>
    </button>
  );
}

export function PwaInstallPrompt({ className }: { className?: string }) {
  const isMobile = useIsMobile();
  const [openIos, setOpenIos] = React.useState(false);
  const [openAndroid, setOpenAndroid] = React.useState(false);

  if (isMobile) {
    return (
      <div className={cn("flex flex-wrap items-center gap-3", className)}>
        <Sheet open={openIos} onOpenChange={setOpenIos}>
          <SheetTrigger asChild>
            <span>
              <PlatformButton platform="ios" />
            </span>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-auto max-h-[85dvh] rounded-t-2xl border-t border-gray-200 bg-white px-6 pb-8 pt-6">
            <SheetHeader className="text-left">
              <SheetTitle className="text-xl font-semibold text-black">Instalar no iPhone</SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              <InstallContent platform="ios" layout="vertical" />
            </div>
          </SheetContent>
        </Sheet>

        <Sheet open={openAndroid} onOpenChange={setOpenAndroid}>
          <SheetTrigger asChild>
            <span>
              <PlatformButton platform="android" />
            </span>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-auto max-h-[85dvh] rounded-t-2xl border-t border-gray-200 bg-white px-6 pb-8 pt-6">
            <SheetHeader className="text-left">
              <SheetTitle className="text-xl font-semibold text-black">Instalar no Android</SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              <InstallContent platform="android" layout="vertical" />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      <Dialog open={openIos} onOpenChange={setOpenIos}>
        <DialogTrigger asChild>
          <span>
            <PlatformButton platform="ios" />
          </span>
        </DialogTrigger>
        <DialogContent className="max-w-lg border-gray-200 bg-white p-6 sm:rounded-2xl">
          <DialogHeader className="text-left">
            <DialogTitle className="text-xl font-semibold text-black">Instalar no iPhone</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <InstallContent platform="ios" layout="horizontal" />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={openAndroid} onOpenChange={setOpenAndroid}>
        <DialogTrigger asChild>
          <span>
            <PlatformButton platform="android" />
          </span>
        </DialogTrigger>
        <DialogContent className="max-w-lg border-gray-200 bg-white p-6 sm:rounded-2xl">
          <DialogHeader className="text-left">
            <DialogTitle className="text-xl font-semibold text-black">Instalar no Android</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <InstallContent platform="android" layout="horizontal" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Versão escura e compacta para o menu mobile — abre sempre em bottom sheet. */
export function InstallAppMenuCard() {
  const [openIos, setOpenIos] = React.useState(false);
  const [openAndroid, setOpenAndroid] = React.useState(false);

  const lightSheet = "rounded-t-2xl border-t border-gray-200 bg-white px-6 pb-8 pt-6 text-black";

  return (
    <div className="overflow-hidden rounded-2xl border border-foreground/10 bg-foreground/[0.03]">
      <div className="flex items-center gap-3 px-3.5 pb-1 pt-3.5">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-foreground/[0.06]">
          <Smartphone className="h-[17px] w-[17px] text-primary" strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-medium">Instalar o app</div>
          <div className="truncate text-[11px] text-muted-foreground">
            Adicione à tela inicial do seu celular
          </div>
        </div>
      </div>
      <div className="flex gap-2 px-3.5 pb-3.5 pt-2">
        <Sheet open={openIos} onOpenChange={setOpenIos}>
          <SheetTrigger asChild>
            <button
              type="button"
              className="flex flex-1 items-center justify-center gap-2 rounded-full border border-foreground/10 bg-foreground/[0.05] px-3 py-2.5 text-[13px] font-semibold transition active:scale-[0.98]"
            >
              <AppleLogo className="h-4 w-4" />
              iPhone
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className={cn(lightSheet, "h-auto max-h-[85dvh]")}>
            <SheetHeader className="text-left">
              <SheetTitle className="text-xl font-semibold text-black">Instalar no iPhone</SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              <InstallContent platform="ios" layout="vertical" />
            </div>
          </SheetContent>
        </Sheet>

        <Sheet open={openAndroid} onOpenChange={setOpenAndroid}>
          <SheetTrigger asChild>
            <button
              type="button"
              className="flex flex-1 items-center justify-center gap-2 rounded-full border border-foreground/10 bg-foreground/[0.05] px-3 py-2.5 text-[13px] font-semibold transition active:scale-[0.98]"
            >
              <AndroidIcon className="h-4 w-4" />
              Android
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className={cn(lightSheet, "h-auto max-h-[85dvh]")}>
            <SheetHeader className="text-left">
              <SheetTitle className="text-xl font-semibold text-black">Instalar no Android</SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              <InstallContent platform="android" layout="vertical" />
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </div>
  );
}

function AppleLogo({ className }: { className?: string }) {
  return (
    <svg width="12" height="15" viewBox="0 0 12 15" className={className} fill="currentColor" aria-hidden="true">
      <path d="M6.08803 3.3535C6.74395 3.3535 7.56615 2.91006 8.05578 2.31881C8.49922 1.78299 8.82256 1.03469 8.82256 0.286386C8.82256 0.184765 8.81332 0.0831445 8.79484 0C8.06502 0.0277148 7.18738 0.489629 6.6608 1.10859C6.24508 1.57975 5.86631 2.31881 5.86631 3.07635C5.86631 3.18721 5.88479 3.29807 5.89402 3.33502C5.94021 3.34426 6.01412 3.3535 6.08803 3.3535ZM3.77846 14.5318C4.67457 14.5318 5.07182 13.9313 6.18965 13.9313C7.32596 13.9313 7.57539 14.5133 8.57313 14.5133C9.55238 14.5133 10.2083 13.608 10.8273 12.7211C11.5201 11.7049 11.8065 10.7072 11.825 10.661C11.7603 10.6425 9.88496 9.87572 9.88496 7.7232C9.88496 5.85707 11.3631 5.01639 11.4462 4.95172C10.467 3.5475 8.97961 3.51055 8.57313 3.51055C7.47377 3.51055 6.57766 4.1757 6.01412 4.1757C5.40439 4.1757 4.60066 3.5475 3.64912 3.5475C1.83842 3.5475 0 5.0441 0 7.87101C0 9.62629 0.683633 11.4832 1.52432 12.6842C2.2449 13.7004 2.87311 14.5318 3.77846 14.5318Z" />
    </svg>
  );
}

function AndroidIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.417.417 0 00-.5676.1521l-2.0225 3.503C15.5902 8.4797 13.853 8.0022 12 8.0022c-1.853 0-3.5902.4775-5.1368 1.3008L4.8407 5.7998a.417.417 0 00-.5676-.1521.415.415 0 00-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6588.3432 18.6622h23.3136c0-4.0034-2.3457-7.4755-5.7748-9.3408" />
    </svg>
  );
}
